import { lazy, Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { NotFoundPage } from "@/pages/public/NotFoundPage";
import HomePage from "@/pages/public/HomePage";
import { PageMeta } from "@/components/shared/PageMeta";

const ProjectsListPage = lazy(() => import("@/pages/public/ProjectsListPage"));
const ProjectDetailPage = lazy(() => import("@/pages/public/ProjectDetailPage"));
const JourneysListPage = lazy(() => import("@/pages/public/JourneysListPage"));
const JourneyDetailPage = lazy(() => import("@/pages/public/JourneyDetailPage"));
const AboutPage = lazy(() => import("@/pages/public/AboutPage"));
const ContactPage = lazy(() => import("@/pages/public/ContactPage"));

const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const AdminProjectsPage = lazy(() => import("@/pages/admin/AdminProjectsPage"));
const AdminProjectFormPage = lazy(() => import("@/pages/admin/AdminProjectFormPage"));
const AdminSkillsPage = lazy(() => import("@/pages/admin/AdminSkillsPage"));
const AdminJourneysPage = lazy(() => import("@/pages/admin/AdminJourneysPage"));
const AdminJourneyFormPage = lazy(() => import("@/pages/admin/AdminJourneyFormPage"));
const AdminMilestonesPage = lazy(() => import("@/pages/admin/AdminMilestonesPage"));
const AdminContactPage = lazy(() => import("@/pages/admin/AdminContactPage"));
const AdminAnalyticsPage = lazy(() => import("@/pages/admin/AdminAnalyticsPage"));

function PageFallback() {
  return (
    <div className="grid min-h-[50vh] place-items-center text-muted-foreground">
      Loading…
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <PageMeta />
      {!isAdmin && <Navbar />}
      <main className="flex-1">
        <Suspense fallback={<PageFallback />}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <Routes location={location}>
                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/projects" element={<ProjectsListPage />} />
                <Route path="/projects/:slug" element={<ProjectDetailPage />} />
                <Route path="/journeys" element={<JourneysListPage />} />
                <Route path="/journeys/:slug" element={<JourneyDetailPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />

                {/* Admin routes — merged into the same tree so they take
                    precedence over the catch-all below */}
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/admin/projects" element={<AdminProjectsPage />} />
                <Route path="/admin/projects/new" element={<AdminProjectFormPage />} />
                <Route path="/admin/projects/:id/edit" element={<AdminProjectFormPage />} />
                <Route path="/admin/skills" element={<AdminSkillsPage />} />
                <Route path="/admin/journeys" element={<AdminJourneysPage />} />
                <Route path="/admin/journeys/new" element={<AdminJourneyFormPage />} />
                <Route path="/admin/journeys/:id/edit" element={<AdminJourneyFormPage />} />
                <Route path="/admin/journeys/:id/milestones" element={<AdminMilestonesPage />} />
                <Route path="/admin/contact" element={<AdminContactPage />} />
                <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />

                {/* Catch-all must be last */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </Suspense>
      </main>
      {!isAdmin && <Footer />}
    </div>
  );
}
